import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api.js";
import { useGame } from "./game.js";

/**
 * "Only talk to the DM when addressed" mode: shows the mode, and a press-and-hold button (or hold Space) that sends
 * the speaker's voice to the DM while held (plus ~1.5 s after letting go, handled by the bot).
 */
export function TalkButton() {
  const { campaign, role } = useGame();
  const [held, setHeld] = useState(false);
  const heldRef = useRef(false);
  const keepAlive = useRef<ReturnType<typeof setInterval> | null>(null);

  const set = useCallback((on: boolean) => {
    if (heldRef.current === on) return;
    heldRef.current = on;
    setHeld(on);
    if (keepAlive.current) clearInterval(keepAlive.current);
    keepAlive.current = on ? setInterval(() => void api.dmPtt(campaign.id, true).catch(() => undefined), 30_000) : null;
    void api.dmPtt(campaign.id, on).catch(() => undefined);
  }, [campaign.id]);

  // Space bar hold (not while typing in a field).
  useEffect(() => {
    if (!campaign.addressedOnly) return;
    const typing = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(t.tagName));
    };
    const down = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat || typing(e)) return;
      e.preventDefault();
      set(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== "Space" || !heldRef.current) return;
      e.preventDefault();
      set(false);
    };
    const blur = () => set(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
      set(false);
    };
  }, [campaign.addressedOnly, set]);

  if (!campaign.addressedOnly || !role) return null;
  return (
    <section className={`panel talk-to-dm${held ? " held" : ""}`}>
      <p className="small">
        <span className="tag">Addressed-only mode</span> The DM only hears you when you say <strong>"Dungeon Master"</strong> or hold the button. Other table talk isn't sent or saved.
      </p>
      <button
        type="button"
        className={held ? "primary talk-btn held" : "talk-btn"}
        aria-pressed={held}
        onPointerDown={(e) => {
          e.preventDefault();
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          set(true);
        }}
        onPointerUp={() => set(false)}
        onPointerCancel={() => set(false)}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); set(true); }
        }}
        onKeyUp={(e) => {
          if (e.key === " " || e.key === "Enter") { e.preventDefault(); set(false); }
        }}
      >
        {held ? "🎙️ The DM is listening… (release to stop)" : "🎙️ Hold to talk to the DM (or hold Space)"}
      </button>
    </section>
  );
}
